// A transition SURFACE is an ordered GROUP of routes rendered as sliding panes: a swipe steps one place, and a
// link to ANY member slides straight there in one screen. Direction falls out of the order, so the array the tab
// bar renders from is the array everything travels by and the two cannot drift.
// The app-agnostic contract only: the actual surface GROUPS live outside this file.
// `RoutePath` is a plain string because a group's members are the CONCRETE pathnames the router navigates to.

/** A concrete, navigable pathname (e.g. `/teams/42/report`). Runtime instances, not route identities. */
export type RoutePath = string

export interface TransitionOutletProps {
  // A link to ANY member slides straight there in one screen, in the direction their order implies.
  group: readonly RoutePath[]
  // Required for a NESTED surface, omitted for the root one: it travels to the preview's shadow router so a layout
  // hosting the surface renders only its <Outlet/> into the pane, and it scopes the debug dump.
  previewFrom?: RoutePath
  // The travelled area, so it should span the full width the routes occupy.
  className?: string
  // Where a route's padding belongs: padding on a container OUTSIDE the stage does not travel with the page.
  contentClassName?: string
  // Opt-in per surface, default OFF: the pane is a shadow router, so what interaction means there is a policy
  // question each surface answers for itself. A native scroll owns every touch until it stops moving, so this
  // can only unlock the landed-but-uncommitted window — see scroll-transition-outlet's header.
  interactiveIncoming?: boolean
}

/** True when `pathname` is on `member` (exact or nested, e.g. `/teams/42/report/x` is on `/teams/42/report`). */
export function matchesFrom(pathname: string, member: string): boolean {
  return pathname === member || pathname.startsWith(member.endsWith('/') ? member : `${member}/`)
}

// Matched by prefix, LONGEST first: /teams/42/board must resolve to Board even if a shorter member also prefixes it.
export function indexOfMember(order: readonly RoutePath[], pathname: string): number {
  let best = -1
  let bestLength = -1
  for (let i = 0; i < order.length; i++) {
    const member = order[i]
    if (!member || !matchesFrom(pathname, member)) continue
    if (member.length > bestLength) {
      best = i
      bestLength = member.length
    }
  }
  return best
}

// The override belongs to whichever surface is sliding, and surfaces NEST — honouring one that targets no member
// of THIS group dims every tab. So take it only when it lands on one of `order`'s members.
export function groupVisualPath(order: readonly string[], override: string | null, realPath: string): string {
  if (override !== null && order.some((member) => matchesFrom(override, member))) return override
  return realPath
}

// Travelling between two members of its own group, or resting at one.
export type MarkerPlacement =
  | { mode: 'travel'; from: string; to: string; progress: number }
  | { mode: 'rest'; key: string }

// It travels only when BOTH endpoints are members of this strip: a slide belonging to another surface has nothing
// here to travel between, so the marker rests under `activePath` and rides away with it.
export function markerPlacement(
  slide: { from: string; to: string; progress: number } | null,
  inGroup: (member: string) => boolean,
  activePath: string,
): MarkerPlacement {
  if (slide && inGroup(slide.from) && inGroup(slide.to)) {
    const progress = slide.progress < 0 ? 0 : slide.progress > 1 ? 1 : slide.progress
    return { mode: 'travel', from: slide.from, to: slide.to, progress }
  }
  return { mode: 'rest', key: activePath }
}
